import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Users, Heart, Baby, User, MoreVertical, Copy, Sparkles, HelpCircle, FileText, Inbox } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import { EmptyStateCoach } from "@/components/EmptyStateCoach";
import ReferralCard from "@/components/ReferralCard";
import ReferralBanner from "@/components/ReferralBanner";
import { GuidedTour, DASHBOARD_TOUR_STEPS, resetTour } from "@/lib/tour";
import { NvcNoticeDashboardCard } from "@/components/NvcNoticeIntake";
import VisaBulletinBanner from "@/components/VisaBulletinBanner";
import { ReadinessStrip, useCaseReadiness } from "@/components/CaseReadinessCard";
import { friendlyError } from "@/lib/errors";

const RELATIONSHIPS = [
  { id: "spouse",  label: "Spouse",  icon: Heart,  desc: "Petitioning for a husband or wife." },
  { id: "child",   label: "Child",   icon: Baby,   desc: "Petitioning for a son or daughter." },
  { id: "parent",  label: "Parent",  icon: User,   desc: "Petitioning for your mother or father." },
  { id: "sibling", label: "Sibling", icon: Users,  desc: "Petitioning for a brother or sister." },
];

export default function Dashboard() {
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [tourKey, setTourKey] = useState(0);
  const [tourForce, setTourForce] = useState(false);
  const [rel, setRel] = useState("spouse");
  const [benName, setBenName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cases");
      setCases(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load your cases. Please refresh the page."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createCase = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/cases", { relationship: rel, beneficiary_name: benName });
      toast.success("New case created — let's fill it in together.");
      nav(`/case/${data.id}`);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't create the case. Try again in a moment."));
    } finally { setCreating(false); }
  };

  return (
    <AppShell>
      <EmailVerificationBanner />
      <GuidedTour tourId="dashboard-v1" steps={DASHBOARD_TOUR_STEPS} key={tourKey} autoStart={tourForce || (cases.length === 0 && !loading)} />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
        <ReferralBanner />
        <VisaBulletinBanner />
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div>
            <div className="pill pill-ai mb-3">Your cases</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold" style={{ color: "var(--text-primary)" }}>
              Family-based petitions
            </h1>
            <p className="mt-2 text-sm sm:text-[15px]" style={{ color: "var(--text-secondary)" }}>
              Every case is guided end-to-end. Pick up right where you left off.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                resetTour("dashboard-v1");
                setTourForce(true);
                setTourKey((k) => k + 1);
                toast.success("Starting the guided tour…");
              }}
              className="btn-ghost text-sm flex items-center gap-1.5"
              style={{ border: "1px solid var(--border-stone)", background: "#FFFFFF" }}
              data-testid="dashboard-show-me-around-btn"
              title="Restart the guided tour"
            >
              <HelpCircle className="w-4 h-4" /> Show me around
            </button>
            <a
              href="/attorney-handout"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-sm flex items-center gap-1.5"
              style={{ border: "1px solid var(--border-stone)", background: "#FFFFFF" }}
              data-testid="dashboard-attorney-handout-btn"
              title="Open the attorney document checklist in a new tab (print-friendly)"
            >
              <FileText className="w-4 h-4" /> Attorney checklist
            </a>
            <Link
              to="/inbox"
              data-testid="dashboard-inbox-link"
              className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border font-medium"
              style={{borderColor:"var(--border)",color:"var(--text-primary)",background:"var(--bg-card)"}}
              title="Upload a stack of NVC or consulate letters — we'll sort them into the right cases"
            >
              <Inbox className="w-4 h-4" /> Bulk Inbox
            </Link>
            <button
              data-testid="new-case-btn"
              className="btn-primary flex items-center gap-2 flex-1 sm:flex-none justify-center"
              onClick={() => setShowNew(true)}
            >
              <Plus className="w-4 h-4" /> Start a new case
            </button>
          </div>
        </div>

        <NvcNoticeDashboardCard />

        {(() => {
          if (loading) {
            return (
              <div className="text-sm" style={{ color: "var(--text-secondary)" }}>Loading…</div>
            );
          }
          if (cases.length === 0) {
            return (
              <EmptyStateCoach
                icon={Sparkles}
                title="Your first case is one click away"
                description="Start by telling us who you're petitioning for. Then upload their documents — the platform reads them, translates them, and fills your forms."
                bullets={[
                  "Works for spouse, child, parent, or sibling petitions",
                  "Speaks 15 languages — Spanish, Hindi, Mandarin, Tagalog & more",
                  "You can pause any time — we save your work as you go",
                ]}
                action={
                  <button data-testid="empty-new-case-btn" className="btn-primary" onClick={() => setShowNew(true)}>
                    <Plus className="w-4 h-4 inline mr-1" /> Start your first case
                  </button>
                }
                testId="empty-state"
              />
            );
          }
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {cases.map((c) => (
                <CaseCard
                  key={c.id}
                  c={c}
                  onCloned={(newCase) => nav(`/case/${newCase.id}`)}
                />
              ))}
            </div>
          );
        })()}

        {/* Always show the full referral card — even brand-new users
             (before they've created a case) should see the $50 program. */}
        <div className="mt-8">
          <ReferralCard />
        </div>

        {showNew && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center p-4"
            style={{ background: "rgba(26,46,36,0.35)" }}
            onClick={() => setShowNew(false)}
          >
            <div
              className="card-surface p-8 max-w-lg w-full anim-in"
              onClick={(e) => e.stopPropagation()}
              data-testid="new-case-modal"
            >
              <h3 className="font-display text-2xl font-bold mb-1">New family-based case</h3>
              <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
                Who are you petitioning for? You can change this later.
              </p>
              <form onSubmit={createCase}>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {RELATIONSHIPS.map((r) => {
                    const Icon = r.icon;
                    const active = rel === r.id;
                    return (
                      <button
                        type="button"
                        key={r.id}
                        data-testid={`rel-${r.id}`}
                        onClick={() => setRel(r.id)}
                        className="text-left p-4 rounded-lg border transition-colors"
                        style={{
                          background: active ? "var(--ai-bg)" : "#fff",
                          borderColor: active ? "var(--ai-border)" : "var(--border-stone)",
                        }}
                      >
                        <Icon className="w-5 h-5 mb-2" style={{ color: active ? "var(--action)" : "var(--text-secondary)" }} />
                        <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{r.label}</div>
                        <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{r.desc}</div>
                      </button>
                    );
                  })}
                </div>

                <label className="field-label">Beneficiary name (optional)</label>
                <input
                  data-testid="new-case-beneficiary-input"
                  className="field-input"
                  placeholder="e.g. Diego Ramirez"
                  value={benName}
                  onChange={(e) => setBenName(e.target.value)}
                />

                <div className="mt-6 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="btn-ghost text-sm"
                    onClick={() => setShowNew(false)}
                    data-testid="new-case-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={creating}
                    data-testid="new-case-submit-btn"
                  >
                    {creating ? "Creating…" : "Create case"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* -------- Case Card + sibling clone menu -------- */
function CaseCard({ c, onCloned }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cloning, setCloning] = useState(false);
  const { readiness } = useCaseReadiness(c.id);

  const displayName =
    c.data?.beneficiary_first_name || c.data?.beneficiary_last_name
      ? `${c.data?.beneficiary_first_name ?? ""} ${c.data?.beneficiary_last_name ?? ""}`.trim()
      : "Untitled beneficiary";

  const clone = async () => {
    setCloning(true);
    setMenuOpen(false);
    try {
      const { data } = await api.post(`/cases/${c.id}/clone`);
      toast.success("Sibling case created with your petitioner details prefilled.");
      onCloned?.(data);
    } catch (err) {
      console.error("[Dashboard] clone failed:", err?.message || err);
      toast.error(friendlyError(err, "We couldn't clone this case. Try again in a moment."));
    } finally {
      setCloning(false);
    }
  };

  return (
    <div
      className="card-surface p-6 anim-in relative"
      data-testid={`case-card-${c.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="pill pill-ai capitalize">{c.relationship}</span>
        <div className="flex items-center gap-2">
          <span
            className="text-[11px] font-mono"
            style={{ color: "var(--text-secondary)" }}
          >
            {c.id.slice(0, 8)}
          </span>
          <button
            type="button"
            data-testid={`case-menu-${c.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="p-1 rounded hover:bg-black/5 transition-colors"
            aria-label="Case actions"
          >
            <MoreVertical className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
      </div>

      <Link to={`/case/${c.id}`} className="block" data-testid={`case-open-${c.id}`}>
        <h3 className="font-display font-bold text-lg leading-snug mb-1">
          {displayName}
        </h3>
        <p className="text-xs mb-4" style={{ color: "var(--text-secondary)" }}>
          DS-260 · {new Date(c.created_at).toLocaleDateString()}
        </p>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: "var(--border-stone)" }}
        >
          <div
            className="h-full transition-all"
            style={{ width: `${c.completion_percent}%`, background: "var(--action)" }}
          />
        </div>
        <div
          className="mt-2 flex items-center justify-between text-xs"
          style={{ color: "var(--text-secondary)" }}
        >
          <span>{c.completion_percent}% complete</span>
          <span>{c.missing_fields?.length ?? 0} pending</span>
        </div>
        {readiness && (
          <div className="mt-3">
            <ReadinessStrip readiness={readiness} />
          </div>
        )}
      </Link>

      {menuOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setMenuOpen(false)}
          />
          <div
            className="absolute right-4 top-12 z-40 card-surface shadow-xl py-1 text-sm"
            style={{ minWidth: 260 }}
            data-testid={`case-menu-panel-${c.id}`}
          >
            <button
              type="button"
              data-testid={`case-clone-btn-${c.id}`}
              className="w-full text-left px-4 py-2 hover:bg-black/5 flex items-center gap-2 transition-colors"
              onClick={clone}
              disabled={cloning}
            >
              <Copy className="w-4 h-4" style={{ color: "var(--action)" }} />
              <span>
                <div className="font-semibold" style={{ color: "var(--text-primary)" }}>
                  {cloning ? "Cloning…" : "Create sibling case for another beneficiary"}
                </div>
                <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  Keeps petitioner data, starts a fresh beneficiary intake.
                </div>
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
